/**
 * Type-effectiveness lookups for the move action menu's persistent
 * effectiveness badge (PR 2.5 / EXP 4). Preload @pkmn/data's Gen-3 type
 * matrix at BattleScene mount, cache module-level, then resolve
 * `effectivenessAgainst(moveType, defenderTypes)` synchronously on every
 * `|switch|p2a|` event + at battle start.
 *
 * Eng review Tension D 2026-05-13: math is the product over
 * `defenderTypes` of `damageTaken[moveType]`, which @pkmn/data exposes
 * via `Type.totalEffectiveness(defenders)`. NOT an inline fork — outside
 * voice argued correctly that inlining a Gen-3 typechart is a land-grab
 * on a foreign dep contract; preload + cache addresses the sync-lookup
 * need without forking.
 *
 * Reduced-bundle posture: the @pkmn/dex import is already eager-warmed
 * at boot (main.ts:37) so loadGen3() is a microsecond cache hit by the
 * time BattleScene mounts. preloadTypeChart() is safe to call from
 * BattleScene.preload() / create().
 */
import type { TypeName } from '@pkmn/data';
import { loadGen3 } from '../data/dex';

type Types = Awaited<ReturnType<typeof loadGen3>>['types'];

let cachedTypes: Types | null = null;

/** Idempotent preload. Calls beyond the first are O(1). */
export async function preloadTypeChart(): Promise<void> {
  if (cachedTypes) return;
  const gen = await loadGen3();
  cachedTypes = gen.types;
}

/** Capitalize a lowercase type id (the codebase convention — 'electric')
 *  to @pkmn/data's TypeName form ('Electric'). The Type registry's
 *  `effectiveness` map keys + `totalEffectiveness` target both expect
 *  capitalized names. */
function capitalizeType(t: string): string {
  if (!t) return t;
  return t[0]!.toUpperCase() + t.slice(1).toLowerCase();
}

/**
 * Resolve the effectiveness multiplier of a move of `moveType` hitting a
 * Pokémon with `defenderTypes`. Returns 1 (neutral) on cache miss or
 * unknown type — defensive default so the UI never renders a stale or
 * incorrect badge while the preload is in flight.
 *
 * Worked examples (eng review Tension D 2026-05-13):
 *   - electric vs [ground]              → 0    (immunity)
 *   - electric vs [water, flying]       → 4    (2 × 2)
 *   - electric vs [rock, ground]        → 0    (1 × 0)
 *   - water    vs [fire]                → 2    (super-effective)
 *   - fire     vs [water, rock]         → 0.25 (0.5 × 0.5 — double-resist)
 */
export function effectivenessAgainst(
  moveType: string,
  defenderTypes: readonly string[],
): number {
  if (!cachedTypes) return 1;
  const type = cachedTypes.get(capitalizeType(moveType));
  if (!type) return 1;
  // totalEffectiveness accepts TypeName | TypeName[] | { types: ... }.
  // The capitalized array IS valid TypeName[] at runtime; the cast
  // through `unknown` keeps the TS literal-union check happy without
  // claiming any specific subset at compile time.
  const targets = defenderTypes.map(capitalizeType) as unknown as TypeName[];
  return type.totalEffectiveness(targets);
}

/** Badge color token resolved from an effectiveness multiplier per the
 *  DESIGN.md component vocab "Effectiveness badge" thresholds. */
export function matchupToken(
  effectiveness: number,
): '--matchup-good' | '--matchup-neutral' | '--matchup-bad' | '--matchup-none' {
  if (effectiveness === 0) return '--matchup-none';
  if (effectiveness < 1) return '--matchup-bad';
  if (effectiveness > 1) return '--matchup-good';
  return '--matchup-neutral';
}

/** Test seam: clear the module-level cache. Used by tests that want a
 *  cold-start state. Not exported as a runtime helper — game code only
 *  preloads, never reloads. */
export function __resetTypeChartForTests(): void {
  cachedTypes = null;
}
